/**
 * @fileoverview Request Deduplication System
 * @module frontend/services/tracking/request-deduplication.ts
 *
 * Prevents duplicate tracking requests by tracking sent request IDs
 * and deduplicating on retry.
 */

/**
 * Configuration for request deduplication
 */
export interface RequestDeduplicationConfig {
  /** Maximum number of request IDs to track (default: 1000) */
  maxTrackedRequests?: number
  /** Time to keep request ID in memory (ms, default: 5 minutes) */
  requestIdTTL?: number
}

/**
 * Tracked request metadata
 */
interface TrackedRequest {
  id: string
  timestamp: number
  eventCount: number
  eventTypes: string[]
}

/**
 * Request Deduplication System
 *
 * Prevents duplicate tracking requests by:
 * 1. Assigning unique request IDs to each batch
 * 2. Tracking sent request IDs
 * 3. Deduplicating on retry (same request ID = already sent)
 * 4. Cleaning up old request IDs to prevent memory leaks
 *
 * @example
 * ```typescript
 * const dedup = new RequestDeduplication()
 * const requestId = dedup.generateRequestId()
 * // Send request with requestId
 * dedup.recordSentRequest(requestId, events)
 * // On retry, check if already sent
 * if (dedup.isRequestSent(requestId)) {
 *   // Skip sending, already sent
 * }
 * ```
 */
export class RequestDeduplication {
  private trackedRequests: Map<string, TrackedRequest> = new Map()
  private config: Required<RequestDeduplicationConfig>
  private cleanupInterval: NodeJS.Timeout | null = null

  constructor(config: RequestDeduplicationConfig = {}) {
    this.config = {
      maxTrackedRequests: config.maxTrackedRequests ?? 1000,
      requestIdTTL: config.requestIdTTL ?? 5 * 60 * 1000, // 5 minutes
    }

    // Start cleanup interval
    this.startCleanupInterval()
  }

  /**
   * Generate a unique request ID
   *
   * @returns Unique request ID
   */
  generateRequestId(): string {
    return `req-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
  }

  /**
   * Record a sent request
   *
   * @param requestId - Request ID
   * @param eventCount - Number of events in the request
   * @param eventTypes - Types of events in the request
   */
  recordSentRequest(requestId: string, eventCount: number, eventTypes: string[]): void {
    // Check if we're at capacity
    if (this.trackedRequests.size >= this.config.maxTrackedRequests) {
      // Remove oldest request
      const oldestKey = Array.from(this.trackedRequests.entries()).sort(
        ([, a], [, b]) => a.timestamp - b.timestamp
      )[0]?.[0]

      if (oldestKey) {
        this.trackedRequests.delete(oldestKey)
      }
    }

    this.trackedRequests.set(requestId, {
      id: requestId,
      timestamp: Date.now(),
      eventCount,
      eventTypes,
    })
  }

  /**
   * Check if a request has already been sent
   *
   * @param requestId - Request ID to check
   * @returns True if request was already sent
   */
  isRequestSent(requestId: string): boolean {
    return this.trackedRequests.has(requestId)
  }

  /**
   * Get metadata for a sent request
   *
   * @param requestId - Request ID
   * @returns Request metadata or undefined if not found
   */
  getRequestMetadata(requestId: string): TrackedRequest | undefined {
    return this.trackedRequests.get(requestId)
  }

  /**
   * Remove a request from tracking (e.g., after successful processing)
   *
   * @param requestId - Request ID to remove
   */
  removeRequest(requestId: string): void {
    this.trackedRequests.delete(requestId)
  }

  /**
   * Clear all tracked requests
   */
  clear(): void {
    this.trackedRequests.clear()
  }

  /**
   * Get count of tracked requests
   *
   * @returns Number of tracked requests
   */
  getTrackedCount(): number {
    return this.trackedRequests.size
  }

  /**
   * Get all tracked request IDs
   *
   * @returns Array of tracked request IDs
   */
  getTrackedRequestIds(): string[] {
    return Array.from(this.trackedRequests.keys())
  }

  /**
   * Start cleanup interval to remove expired request IDs
   */
  private startCleanupInterval(): void {
    // Run cleanup every minute
    this.cleanupInterval = setInterval(() => {
      this.cleanup()
    }, 60 * 1000)

    // Don't keep the process alive
    if (this.cleanupInterval.unref) {
      this.cleanupInterval.unref()
    }
  }

  /**
   * Clean up expired request IDs
   */
  private cleanup(): void {
    const now = Date.now()
    const expiredIds: string[] = []

    for (const [id, request] of this.trackedRequests.entries()) {
      if (now - request.timestamp > this.config.requestIdTTL) {
        expiredIds.push(id)
      }
    }

    expiredIds.forEach(id => this.trackedRequests.delete(id))

    if (expiredIds.length > 0) {
      console.debug(`🧹 Cleaned up ${expiredIds.length} expired request IDs`)
    }
  }

  /**
   * Stop cleanup interval
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
      this.cleanupInterval = null
    }
    this.trackedRequests.clear()
  }

  /**
   * Get statistics about tracked requests
   *
   * @returns Statistics object
   */
  getStats(): {
    trackedCount: number
    maxTracked: number
    totalEventsSent: number
    uniqueEventTypes: Set<string>
  } {
    let totalEventsSent = 0
    const uniqueEventTypes = new Set<string>()

    for (const request of this.trackedRequests.values()) {
      totalEventsSent += request.eventCount
      request.eventTypes.forEach(type => uniqueEventTypes.add(type))
    }

    return {
      trackedCount: this.trackedRequests.size,
      maxTracked: this.config.maxTrackedRequests,
      totalEventsSent,
      uniqueEventTypes,
    }
  }
}

